import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

interface ConsentRecord {
  id: string;
  phoneNumber: string;
  consentType: string;
  consentedAt: string;
  isActive: boolean;
  revokedAt?: string;
}

const SmsConsentSettings: React.FC = () => {
  const { t } = useTranslation();
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConsents();
  }, []);

  const fetchConsents = async () => {
    try {
      const response = await axios.get('/sms-consent');
      setConsents(response.data);
    } catch (error) {
      console.error('Failed to fetch consents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (consentId: string) => {
    if (
      !window.confirm(
        t(
          'sms.confirmRevoke',
          'Are you sure you want to revoke SMS consent? You will stop receiving messages.',
        ),
      )
    ) {
      return;
    }

    try {
      await axios.delete(`/sms-consent/${consentId}`, {
        data: { reason: 'User requested revocation via settings' },
      });
      fetchConsents();
      alert(t('sms.revokeSuccess', 'SMS consent has been revoked successfully.'));
    } catch (error) {
      console.error('Failed to revoke consent:', error);
      alert(t('sms.revokeError', 'Failed to revoke consent. Please try again.'));
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get('/sms-consent/download', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sms-consent-records.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download consent records:', error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-400">
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
        {t('sms.settings.title', 'SMS Consent Management')}
      </h2>

      <p className="mb-6 text-gray-700 dark:text-gray-300">
        {t(
          'sms.settings.description',
          'View and manage your SMS consent preferences. You can revoke consent at any time.',
        )}
      </p>

      {consents.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('sms.settings.noConsents', 'You have not provided SMS consent yet.')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {consents.map((consent) => (
            <div
              key={consent.id}
              className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md border-2 ${
                consent.isActive ? 'border-green-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {consent.phoneNumber}
                  </span>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    consent.isActive
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}
                >
                  {consent.isActive
                    ? t('sms.status.active', 'Active')
                    : t('sms.status.revoked', 'Revoked')}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <p>
                  <strong className="font-medium">{t('sms.type', 'Type')}:</strong>{' '}
                  {consent.consentType === 'reachout_program'
                    ? t('sms.types.reachout', 'SMS Reachout Program')
                    : t('sms.types.signup', 'Account Signup')}
                </p>
                <p>
                  <strong className="font-medium">{t('sms.consentedAt', 'Consented At')}:</strong>{' '}
                  {new Date(consent.consentedAt).toLocaleString()}
                </p>
                {consent.revokedAt && (
                  <p>
                    <strong className="font-medium">{t('sms.revokedAt', 'Revoked At')}:</strong>{' '}
                    {new Date(consent.revokedAt).toLocaleString()}
                  </p>
                )}
              </div>

              {consent.isActive && (
                <button
                  onClick={() => handleRevoke(consent.id)}
                  className="mt-4 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  {t('sms.revokeConsent', 'Revoke Consent')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-8">
        <button
          onClick={handleDownload}
          className="bg-purple-600 text-white py-2 px-6 rounded-md hover:bg-purple-700 transition-colors"
        >
          {t('sms.downloadRecords', 'Download Consent Records')}
        </button>
      </div>

      <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
          {t('sms.help.title', 'SMS Commands')}
        </h3>
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li>
            <strong className="font-medium">STOP:</strong>{' '}
            {t('sms.help.stop', 'Opt out of all SMS messages')}
          </li>
          <li>
            <strong className="font-medium">HELP:</strong>{' '}
            {t('sms.help.help', 'Get help and support information')}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SmsConsentSettings;
